import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FormikField } from "@air/formik-field";
import { InfoFilled } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { Input } from "@air/primitive-input";
import { Modal, ModalTitle } from "@air/primitive-modal";
import { Tooltip } from "@air/primitive-tooltip";
import { uuidRegExpString } from "@air/utils-regex";
import { Form, Formik } from "formik";
import { memo, useCallback, useEffect, useState } from "react";
import { object, string } from "yup";
import { useSysadmin } from "./SysadminProvider";
export const SYSADMIN_SET_WORKSPACE_MODAL_LOCAL_STORAGE_KEY = "air/sysadmin/recentlyUsed";
export const SYSADMIN_SET_WORKSPACE_MODAL_VALIDATION = object().shape({
    workspaceId: string().matches(new RegExp(".*(".concat(uuidRegExpString, ").*"), "i"), "Invalid workspace ID").required("Workspace ID is required")
});
export const InternalSetWorkspaceModal = /*#__PURE__*/ memo(()=>{
    const { handleSetWorkspaceId } = useSysadmin();
    const [recentlyUsed, setRecentlyUsed] = useState([]);
    const handleClearRecentlyUsed = useCallback(()=>{
        localStorage.removeItem(SYSADMIN_SET_WORKSPACE_MODAL_LOCAL_STORAGE_KEY);
        setRecentlyUsed([]);
    }, []);
    const handleSetWorkspace = useCallback(async (param, formikHelpers)=>{
        let { workspaceId } = param;
        formikHelpers === null || formikHelpers === void 0 ? void 0 : formikHelpers.setSubmitting(true);
        if (workspaceId) {
            handleSetWorkspaceId(workspaceId, true);
        }
        formikHelpers === null || formikHelpers === void 0 ? void 0 : formikHelpers.setSubmitting(true);
    }, [
        handleSetWorkspaceId
    ]);
    /**
   * Load recently used workspaces from local storage.
   */ useEffect(()=>{
        const recentlyUsed = localStorage.getItem(SYSADMIN_SET_WORKSPACE_MODAL_LOCAL_STORAGE_KEY);
        if (recentlyUsed) {
            setRecentlyUsed(JSON.parse(recentlyUsed));
        }
    }, []);
    /**
   * If the user has a workspace id in the URL, we'll set the workspace id
   * regardless if the user opens the modal or not.
   */ useEffect(()=>{
        const searchParams = new URLSearchParams(window.location.search);
        const workspaceId = searchParams.get("wsid");
        if (workspaceId) {
            handleSetWorkspace({
                workspaceId
            });
        }
    }, [
        handleSetWorkspace
    ]);
    return /*#__PURE__*/ _jsxs(Modal, {
        isOpen: true,
        children: [
            /*#__PURE__*/ _jsx("header", {
                className: "-mx-6 -mt-6 mb-6 rounded-t-6 bg-pigeon-900 p-6 text-white",
                children: /*#__PURE__*/ _jsx(ModalTitle, {
                    className: "text-white",
                    children: "Workspace ID"
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-6",
                children: [
                    /*#__PURE__*/ _jsx(Formik, {
                        initialValues: {
                            workspaceId: ""
                        },
                        onSubmit: handleSetWorkspace,
                        validationSchema: SYSADMIN_SET_WORKSPACE_MODAL_VALIDATION,
                        children: (param)=>{
                            let { isSubmitting } = param;
                            return /*#__PURE__*/ _jsxs(Form, {
                                className: "flex flex-col gap-6",
                                children: [
                                    /*#__PURE__*/ _jsx(FormikField, {
                                        component: /*#__PURE__*/ _jsx(Input, {
                                            required: true,
                                            type: "text",
                                            size: "large"
                                        }),
                                        id: "workspaceId",
                                        label: "Workspace ID",
                                        name: "workspaceId"
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        className: "bg-pigeon-900 hover:bg-pigeon-800",
                                        isLoading: isSubmitting,
                                        size: "large",
                                        type: "submit",
                                        children: "Go to workspace"
                                    })
                                ]
                            });
                        }
                    }),
                    !!recentlyUsed.length && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-px w-full bg-pigeon-100"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-between gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-14 font-semibold",
                                                children: "Recently used"
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                className: "border-0 bg-none text-12 font-semibold hover:underline",
                                                onClick: handleClearRecentlyUsed,
                                                children: "Clear all"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex flex-wrap gap-2",
                                        children: recentlyUsed.map((workspace)=>/*#__PURE__*/ _jsxs("button", {
                                                className: "flex h-8 cursor-pointer items-center gap-2 rounded-4 border border-pigeon-100 bg-pigeon-50 px-3 text-12 font-semibold",
                                                onClick: ()=>handleSetWorkspace({
                                                        workspaceId: workspace.id
                                                    }),
                                                children: [
                                                    workspace.name,
                                                    /*#__PURE__*/ _jsx(Tooltip, {
                                                        label: workspace.id,
                                                        children: /*#__PURE__*/ _jsx(InfoFilled, {
                                                            className: "size-4"
                                                        })
                                                    })
                                                ]
                                            }, workspace.id))
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
InternalSetWorkspaceModal.displayName = "InternalSetWorkspaceModal";
